import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, Gift, Mail, User } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import {
  Button,
  ErrorMessage,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Spinner,
  cn,
} from '@finmin/ui';
import type { SignupDetailsValues } from '../types';
import { PhoneField } from './phone-field';

// Brand tokens — matches the rest of the signup / questionnaire experience.
const PALETTE_FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const PALETTE_MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';

interface SignupDetailsStepProps {
  isPending: boolean;
  apiError: string | null;
  /** Code carried in by an invite link — shows an "applied" confirmation. */
  referralFromLink?: string | null;
  onSubmit: (values: SignupDetailsValues) => void;
}

/**
 * Signup step 1 — personal details.
 * Caller wraps with <Form {...form}> so RHF context is available.
 */
export function SignupDetailsStep({ isPending, apiError, referralFromLink, onSubmit }: SignupDetailsStepProps) {
  const form = useFormContext<SignupDetailsValues>();
  const nameRef = useRef<HTMLInputElement | null>(null);

  // Terms & Privacy acceptance — locally managed because it never leaves the
  // client (no API field). The Continue button is disabled until checked.
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => nameRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, []);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-5">
      {/* Heading */}
      <div className="space-y-1">
        <h2 className="font-display font-medium text-2xl tracking-tight text-foreground">
          Create your account.
        </h2>
        <p className="text-sm text-muted-foreground">
          We&apos;ll send an OTP to verify your number.
        </p>
      </div>

      {/* Full name */}
      <FormField
        control={form.control}
        name="full_name"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>
              Full name{' '}
              <span className="text-destructive" aria-label="required">*</span>
            </FormLabel>
            <FormControl>
              <div
                className={cn(
                  'flex items-center rounded-md border bg-background ring-offset-background transition-all duration-150',
                  fieldState.error
                    ? 'border-destructive focus-within:ring-2 focus-within:ring-destructive/30 focus-within:ring-offset-2'
                    : 'border-input focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                )}
              >
                <span className="pl-3 pr-2 border-r border-input text-muted-foreground shrink-0">
                  <User className="h-3.5 w-3.5" aria-hidden />
                </span>
                <input
                  {...field}
                  ref={(el) => {
                    field.ref(el);
                    nameRef.current = el;
                  }}
                  type="text"
                  autoComplete="name"
                  placeholder="Rohan Sharma"
                  disabled={isPending}
                  className="flex-1 h-10 px-3 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Mobile number */}
      <FormField
        control={form.control}
        name="mobile_e164"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>
              Mobile number{' '}
              <span className="text-destructive" aria-label="required">*</span>
            </FormLabel>
            <FormControl>
              <PhoneField
                value={field.value}
                onChange={(val) => field.onChange(val)}
                onBlur={field.onBlur}
                name={field.name}
                id={field.name}
                hasError={!!fieldState.error}
                disabled={isPending}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Email */}
      <FormField
        control={form.control}
        name="email"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>Email address</FormLabel>
            <FormControl>
              <div
                className={cn(
                  'flex items-center rounded-md border bg-background ring-offset-background transition-all duration-150',
                  fieldState.error
                    ? 'border-destructive focus-within:ring-2 focus-within:ring-destructive/30 focus-within:ring-offset-2'
                    : 'border-input focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                )}
              >
                <span className="pl-3 pr-2 border-r border-input text-muted-foreground shrink-0">
                  <Mail className="h-3.5 w-3.5" aria-hidden />
                </span>
                <Input
                  {...field}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  disabled={isPending}
                  className="border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 h-10"
                />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Referral code (optional) */}
      <FormField
        control={form.control}
        name="referral_code"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>
              Referral code{' '}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </FormLabel>
            <FormControl>
              <div
                className={cn(
                  'flex items-center rounded-md border bg-background ring-offset-background transition-all duration-150',
                  fieldState.error
                    ? 'border-destructive focus-within:ring-2 focus-within:ring-destructive/30 focus-within:ring-offset-2'
                    : 'border-input focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
                )}
              >
                <span className="pl-3 pr-2 border-r border-input text-muted-foreground shrink-0">
                  <Gift className="h-3.5 w-3.5" aria-hidden />
                </span>
                <Input
                  {...field}
                  type="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  placeholder="Have a friend's code?"
                  disabled={isPending}
                  className="border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 h-10 uppercase placeholder:normal-case placeholder:tracking-normal tracking-[0.15em]"
                />
              </div>
            </FormControl>
            {/* Confirmation when the code arrived via an invite link and the
                user hasn't changed it — reassures them the invite "worked". */}
            {referralFromLink && field.value?.trim().toUpperCase() === referralFromLink ? (
              <p className="flex items-center gap-1.5 text-xs text-success mt-1">
                <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Invite code <span className="font-semibold tracking-[0.1em]">{referralFromLink}</span> applied
              </p>
            ) : (
              <FormMessage />
            )}
          </FormItem>
        )}
      />

      {/* API-level error */}
      <ErrorMessage message={apiError} />

      {/* Terms & Privacy acceptance — must be ticked before the Continue
          button is enabled. Click anywhere on the row to toggle. */}
      <label
        className={cn(
          'flex items-start gap-3 cursor-pointer rounded-xl border-2 p-3.5 transition-colors',
          isPending && 'opacity-60 cursor-not-allowed',
        )}
        style={{
          borderColor:     termsAccepted ? PALETTE_FOREST : 'hsl(var(--border))',
          backgroundColor: termsAccepted ? PALETTE_MINT   : 'transparent',
        }}
      >
        <div className="mt-0.5 shrink-0">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            disabled={isPending}
            className="sr-only"
            aria-label="Accept terms of use and privacy policy"
          />
          <div
            className="h-5 w-5 rounded flex items-center justify-center transition-colors"
            style={{
              backgroundColor: termsAccepted ? PALETTE_FOREST : 'transparent',
              border:          termsAccepted ? 'none' : '2px solid hsl(var(--border))',
            }}
          >
            {termsAccepted && <Check className="h-3.5 w-3.5 text-white" aria-hidden />}
          </div>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">
          By continuing, you confirm that you have read, understood, and agree to our{' '}
          <Link
            to="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium underline-offset-2 hover:underline"
            style={{ color: PALETTE_FOREST }}
          >
            Terms of Use
          </Link>{' '}
          and{' '}
          <Link
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium underline-offset-2 hover:underline"
            style={{ color: PALETTE_FOREST }}
          >
            Privacy Policy
          </Link>
          .
        </p>
      </label>

      {/* Submit — gated on `termsAccepted` so the user must opt in first. */}
      <Button
        type="submit"
        className="w-full gap-2"
        size="lg"
        disabled={isPending || !termsAccepted}
      >
        {isPending ? (
          <>
            <Spinner className="h-4 w-4" />
            Sending code…
          </>
        ) : (
          'Continue'
        )}
      </Button>

      {/* Sign-in link */}
      <p className="text-sm text-center text-muted-foreground">
        Already have an account?{' '}
        <Link
          to="/login"
          className="font-medium text-primary hover:underline underline-offset-4 transition-colors"
        >
          Sign in
        </Link>
      </p>
    </form>
  );
}
